import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { TargetType } from '@/types/TargetType.ts';
import { formatPhoneNumber } from '@/types/PhoneNumberDto.utils.ts';
import { PlannedTransactionDetailDto } from '@/features/dashboard/plannedTransactions/types/PlannedTransactionDetailDto.ts';
import {
  BillLine,
  BillSection,
  ClientBill,
  BookingBill,
  ParticipantBill,
} from '@/features/dashboard/bookingChanges/types/BookingBill.ts';
import { BillPaymentDto } from '@/features/dashboard/bookingChanges/types/dto/BillPaymentDto.ts';
import {
  ChangeItemDto,
  ChangeItemType,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';
import {
  ClientBillVm,
  BookingBillVm,
  BillChipVm,
  BillRowAction,
  BillRowBeforeVm,
  BillRowVm,
  BillSectionVm,
  BillTotalsVm,
  InstallmentVm,
  ParticipantBillVm,
  PaymentVm,
  PersonIdentityVm,
  PersonTileVm,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import {
  buildPersonIdentity,
  CLIENT_ROLE_DETAIL,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.utils.ts';
import {
  ChangePreview,
  previewParticipantDelta,
  previewRowImpact,
} from '@/features/dashboard/bookingChanges/types/ChangePreview.ts';
import {
  countItemsForPerson,
  findItemForLine,
  findItemsForLine,
  resolveNewChips,
  resolveNewLabel,
  resolveNewValue,
  sectionsWithDraftLines,
} from '@/features/dashboard/bookingChanges/utils/draftItems.ts';
import {
  addMoneyLists,
  isZeroMoneyList,
  sumAmounts,
} from '@/features/dashboard/bookingChanges/utils/money.ts';
import {
  formatDate,
  formatDateRange,
  formatFullDateTime,
  formatMoneyTransition,
} from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import {
  pluralizeChanges,
  pluralizeYears,
} from '@/features/dashboard/bookingChanges/utils/plurals.ts';
import { EMPTY_VALUE } from '@/components/ui/EmptyValue.tsx';

/**
 * Mapper rachunku rezerwacji na model widoku.
 *
 * Rachunek opisuje stan **sprzed** zmian, a pozycje szkicu — stan docelowy;
 * dopiero tutaj powstają wiersze niosące jedno i drugie (`value`/`oldValue`,
 * `amount`/`oldAmount`), na których komponent buduje podgląd i tryb edycji bez
 * przeskakiwania układu.
 *
 * Napisy „po” nakładają pozycje paczki (`utils/draftItems.ts`); **kwoty „po”
 * przychodzą policzone** z podglądu paczki (`ChangePreview`). `preview === null`
 * przy niepustym szkicu znaczy „kwot nie znamy” — wiersz zostaje wtedy przy
 * kwocie bieżącej, zamiast udawać, że paczka nic nie kosztuje.
 *
 * Każda kwota modelu widoku jest listą po jednej pozycji na walutę: rezerwacja
 * bywa wielowalutowa, a suma 200 zł i 333 € to dwie liczby obok siebie, nie
 * jedna. Waluty się nie mieszają i nie przelicza się ich po kursie.
 *
 * Wywołania należą do hooków, nie do komponentów.
 */

/** Rachunek uczestnika prosto z księgi (`totalsByParticipant`), per waluta. */
const participantTotal = (
  bill: BookingBill,
  participantId: string | null,
): MoneyValueDto[] =>
  bill.totalsByParticipant.find(
    (entry) => entry.participantId === participantId,
  )?.totals ?? [];

/** Wpłacono — saldo rezerwacji per waluta, nie suma wierszy listy wpłat. */
const paidTotal = (bill: BookingBill): MoneyValueDto[] =>
  bill.balances.map((balance) => balance.balance.paid);

const rowAction = (line: BillLine, changed: boolean): BillRowAction => {
  if (changed) {
    return BillRowAction.EDIT;
  }
  if (line.locked) {
    return BillRowAction.OPEN;
  }
  if (line.muted) {
    return BillRowAction.ADD;
  }
  return BillRowAction.CHANGE;
};

/** Chipy z oznaczeniem, co szkic dokłada, a co zabiera. */
const buildChips = (
  currentChips: string[] | null,
  newChips: string[] | null,
): BillChipVm[] | undefined => {
  if (!currentChips) {
    return undefined;
  }
  if (!newChips) {
    return currentChips.map((label) => ({ label }));
  }

  const added = newChips
    .filter((label) => !currentChips.includes(label))
    .map((label) => ({ label, added: true }));
  const kept = currentChips.map((label) => ({
    label,
    removed: newChips.includes(label) ? undefined : true,
  }));

  return [...kept, ...added];
};

/**
 * Przekreślona wartość „przed” — tylko wtedy, gdy szkic naprawdę podmienia
 * napis.
 *
 * Dwa wyjątki, oba widoczne w projekcie:
 * - wiersz z chipami niesie stan sprzed zmiany w samych chipach (dochodzące są
 *   bursztynowe, znikające przekreślone), więc druga, przekreślona lista obok
 *   nich byłaby powtórzeniem;
 * - zmiana samej kwoty (cena bazowa, nadpisanie ceny opcji) zostawia napis bez
 *   zmian — przekreślenie go pokazywałoby tę samą wartość dwa razy.
 */
const resolveOldValue = (
  line: BillLine,
  newValue: string | null,
): string | undefined => {
  if (line.chips || newValue === null || newValue === line.value) {
    return undefined;
  }
  return line.value ?? undefined;
};

/** Wszystko, co mapowanie wiersza czyta poza samym wierszem. */
interface BillMapperContext {
  bill: BookingBill;
  items: ChangeItemDto[];
  preview: ChangePreview | null;
}

const toRow = (
  line: BillLine,
  participantId: string | null,
  withAmounts: boolean,
  context: BillMapperContext,
  /** Wiersz, którego rachunek nie zna — dopisała go pozycja ręczna ze szkicu. */
  draftOnly = false,
): BillRowVm => {
  const { bill, items, preview } = context;
  // Wiersz wielowartościowy niesie tyle pozycji paczki, ile wariantów operator
  // dołożył albo zdjął — dopiero wszystkie razem opisują jego stan „po”.
  const lineItems = findItemsForLine(items, line, participantId);
  const [item] = lineItems;
  const changed = lineItems.length > 0;

  const newValue = changed
    ? resolveNewValue(line, lineItems, bill.catalog)
    : null;
  const newChips = changed
    ? resolveNewChips(line, lineItems, bill.catalog)
    : null;
  // Kwota „po” = kwota z księgi przesunięta o deltę podglądu. Ile pozycji
  // księgi wierszowi zostaje, rozstrzyga, czy zniknie z rachunku (`REMOVE` /
  // `CLEAR`) — zero pozycji to co innego niż pozycja o kwocie zerowej.
  const impact = previewRowImpact(preview, line.costLineIds, line.costKey);
  const repriced = impact.removed > 0 || impact.added > 0;
  const linesAfter = line.costLineIds.length - impact.removed + impact.added;
  const newAmount =
    linesAfter > 0 ? addMoneyLists(line.amount, impact.delta) : [];
  // Wiersz ruszony szkicem mówi o ręcznej kwocie tym, co niesie pozycja paczki;
  // wiersz nietknięty — tym, co zostało w rachunku po paczkach wprowadzonych
  // wcześniej. Bez drugiego członu znacznik ✎ gasł w chwili wprowadzenia paczki.
  const overridden = item
    ? 'priceOverride' in item && item.priceOverride !== null
    : line.priceOverridden;
  // Tą samą drogą co sam znacznik. Pozycja ręczna go nie dostaje — dla niej
  // `priceNote` stoi już w kolumnie wartości.
  const priceNote = line.manual
    ? null
    : item
      ? 'priceNote' in item
        ? item.priceNote
        : null
      : line.priceNote;

  const amount = repriced ? newAmount : line.amount;
  const amountLabel = repriced ? undefined : (line.amountLabel ?? undefined);
  // Pozycja bez kwoty — niewybrana albo zdjęta szkicem (`REMOVE` / `CLEAR`) —
  // dostaje myślnik, a nie pustą komórkę (projekt A1/A2: „Transport”,
  // „Wypożyczenie sprzętu”).
  const withoutAmount = withAmounts && amount.length === 0 && !amountLabel;

  return {
    id: line.id,
    type: line.type,
    // Etykietę rusza tylko pozycja ręczna — nazwę nadaje jej operator w opisie.
    label: (changed ? resolveNewLabel(lineItems) : null) ?? line.label,
    value: newValue ?? line.value ?? undefined,
    chips: buildChips(line.chips, newChips),
    oldValue: changed ? resolveOldValue(line, newValue) : undefined,
    changed: changed || undefined,
    overridden: overridden || undefined,
    priceNote: (overridden && priceNote) || undefined,
    muted: (line.muted && !changed) || undefined,
    selectedAt: changed ? undefined : line.selectedAt,
    amount,
    oldAmount: repriced ? line.amount : [],
    amountMuted: withoutAmount || (line.amountMuted && !changed) || undefined,
    amountLabel: withoutAmount ? EMPTY_VALUE : amountLabel,
    locked: line.locked || undefined,
    manual: line.manual || undefined,
    draftOnly: draftOnly || undefined,
    action: rowAction(line, changed),
    before: changed
      ? rowBefore(line, participantId, withAmounts, context)
      : undefined,
  };
};

/**
 * Wiersz policzony tak, jakby szkicu nie było — to jest stan, do którego wraca
 * podgląd. Liczymy go tą samą funkcją co wiersz właściwy, żeby prezentacja
 * „przed” nie rozjechała się z „po” przy każdej zmianie reguł mapowania.
 */
const rowBefore = (
  line: BillLine,
  participantId: string | null,
  withAmounts: boolean,
  context: BillMapperContext,
): BillRowBeforeVm => {
  const { value, chips, amount, amountLabel, amountMuted, muted, selectedAt } =
    toRow(line, participantId, withAmounts, {
      ...context,
      items: [],
      preview: null,
    });

  return { value, chips, amount, amountLabel, amountMuted, muted, selectedAt };
};

/**
 * Suma sekcji. Jedyne zestawienie, którego backend policzyć nie może: sekcja
 * jest wymysłem ekranu, więc jej suma powstaje z kwot wierszy, które ekran
 * właśnie pokazuje. Wszystkie pozostałe sumy (rachunek uczestnika, wartość
 * rezerwacji, wpłacono) przychodzą policzone.
 */
const sectionSum = (
  section: BillSection,
  rows: { amount?: MoneyValueDto[] | null }[],
): MoneyValueDto[] => (section.withAmounts ? sumAmounts(rows) : []);

const toSection = (
  section: BillSection,
  participantId: string | null,
  context: BillMapperContext,
  /** Wiersze rachunku sprzed doklejenia pozycji ręcznych ze szkicu. */
  billLineIds?: Set<string>,
): BillSectionVm => {
  const rows = section.lines.map((line) =>
    toRow(
      line,
      participantId,
      section.withAmounts,
      context,
      billLineIds ? !billLineIds.has(line.id) : false,
    ),
  );
  // Suma sprzed zmian liczy się z kwot wierszy, a wiersz dopisany szkicem żadnej
  // jeszcze nie ma (`manualLineOf`), więc doklejenie go tej sumy nie rusza.
  return {
    id: section.id,
    title: section.title,
    hint: section.hint ?? undefined,
    sum: sectionSum(section, rows),
    sumBefore: sectionSum(section, section.lines),
    note: section.note ?? undefined,
    rows,
    allowAdd: section.allowManualLines || undefined,
  };
};

/**
 * Sekcje jednej osoby (albo całej rezerwacji) razem z wierszami, które dopisuje
 * szkic. Identyfikatory wierszy rachunku zbieramy przed doklejeniem, bo tylko
 * one odróżniają pozycję istniejącą od dopisanej — a od tego zależy, czy wiersz
 * ma być widoczny w podglądzie.
 */
const toSections = (
  sections: BillSection[],
  participantId: string | null,
  context: BillMapperContext,
): BillSectionVm[] => {
  const billLineIds = new Set(
    sections.flatMap((section) => section.lines).map((line) => line.id),
  );

  return sectionsWithDraftLines(sections, context.items, participantId).map(
    (section) => toSection(section, participantId, context, billLineIds),
  );
};

/** Wiek na kaflu; bez daty urodzenia nie ma czego policzyć. */
const participantRoleDetail = (age: number | null): string =>
  age === null ? 'wiek nieznany' : pluralizeYears(age);

const participantIdentity = (participant: ParticipantBill): PersonIdentityVm =>
  buildPersonIdentity({
    id: participant.participantId,
    kind: TargetType.PARTICIPANT,
    firstName: participant.firstName,
    lastName: participant.lastName,
    roleDetail: participantRoleDetail(participant.age),
  });

const clientIdentity = (client: ClientBill): PersonIdentityVm =>
  buildPersonIdentity({
    id: client.clientId,
    kind: TargetType.CLIENT,
    firstName: client.firstName,
    lastName: client.lastName,
    roleDetail: CLIENT_ROLE_DETAIL,
  });

/**
 * Wartość po uwzględnieniu szkicu = stan z księgi przesunięty o wpływ paczki.
 * Rachunku nie składamy od nowa: obie liczby podaje backend — „przed” księga,
 * przesunięcie podgląd paczki.
 */
const bookingTotalAfter = (
  bill: BookingBill,
  preview: ChangePreview | null,
): MoneyValueDto[] => addMoneyLists(bill.totals, preview?.total ?? []);

/**
 * Nazwy raty backend nie nadaje (`description` bywa puste), a kolumna „Rata"
 * stała pusta na każdym ekranie rezerwacji. Numer idzie więc z **terminu**, nie
 * z pozycji na liście: rezerwacja wielowalutowa ma po jednej racie na walutę
 * w tym samym terminie, a klient widzi je jako jedną „Ratę 1" i tak samo mają
 * się nazywać u operatora. Własny opis, gdy jest, wygrywa z numerem.
 */
const installmentNames = (
  installments: PlannedTransactionDetailDto[],
): Map<string, string> => {
  const dueDates = [...new Set(installments.map((one) => one.dueDate))].sort();

  return new Map(
    installments.map((installment) => [
      installment.id,
      installment.description ??
        `Rata ${dueDates.indexOf(installment.dueDate) + 1}`,
    ]),
  );
};

const toInstallment = (
  installment: PlannedTransactionDetailDto,
  names: Map<string, string>,
): InstallmentVm => ({
  id: installment.id,
  name: names.get(installment.id) ?? '',
  dueDate: installment.dueDate,
  amount: [installment.amount],
  status: installment.paymentStatus,
});

/**
 * Przelew rozpisany na kilka rezerwacji albo częściowo nieprzypisany. Moduły,
 * bo zwrot niesie ujemną alokację przy dodatniej kwocie transakcji.
 */
const isSplitTransfer = (payment: BillPaymentDto): boolean =>
  payment.totalValue.currency === payment.amount.currency &&
  Math.abs(payment.totalValue.amount) !== Math.abs(payment.amount.amount);

const toPayment = (payment: BillPaymentDto): PaymentVm => ({
  id: payment.id,
  paidAt: payment.paidAt,
  method: payment.method,
  reference: payment.reference ?? undefined,
  amount: [payment.amount],
  tags: payment.tags,
  transferTotal: isSplitTransfer(payment) ? [payment.totalValue] : undefined,
});

/** Etykiety pól zapisującego ruszonych w szkicu — dopisek „· e-mail” na kaflu. */
const changedClientFields = (
  bill: BookingBill,
  items: ChangeItemDto[],
): string[] =>
  bill.client.sections
    .flatMap((section) => section.lines)
    .filter(
      (line) =>
        line.ref?.kind === ChangeItemType.CLIENT_DATA &&
        !!findItemForLine(items, line, null),
    )
    .map((line) => line.label.toLowerCase());

export const bookingBillMapper = {
  /** Ekran uczestnika (A1/A2) oraz treść rozwiniętego kafla uczestnika. */
  toParticipantView(
    bill: BookingBill,
    participantId: string,
    items: ChangeItemDto[] = [],
    preview: ChangePreview | null = null,
  ): ParticipantBillVm | null {
    const participant = bill.participants.find(
      (candidate) => candidate.participantId === participantId,
    );
    if (!participant) {
      return null;
    }

    const context = { bill, items, preview };
    const sections = toSections(participant.sections, participantId, context);

    const before = participantTotal(bill, participantId);
    const delta = previewParticipantDelta(preview, participantId);
    const after = addMoneyLists(before, delta);
    const bookingBefore = bill.totals;
    const bookingAfter = bookingTotalAfter(bill, preview);

    const totals: BillTotalsVm = {
      before,
      delta,
      after,
      shareBefore: {
        participantAmount: before,
        bookingAmount: bookingBefore,
      },
      share: {
        participantAmount: after,
        bookingAmount: bookingAfter,
      },
    };

    return {
      person: participantIdentity(participant),
      sections,
      totals,
      draftItemCount: countItemsForPerson(items, participantId),
    };
  },

  /** Ekran osoby zapisującej (B1/B2) — bez kolumny kwot. */
  toClientView(bill: BookingBill, items: ChangeItemDto[] = []): ClientBillVm {
    // Sekcje zapisującego nie niosą kwot, więc podglądu paczki nie potrzebują.
    return {
      person: clientIdentity(bill.client),
      sections: toSections(bill.client.sections, null, {
        bill,
        items,
        preview: null,
      }),
      draftItemCount: countItemsForPerson(items, null),
    };
  },

  /** Ekran rezerwacji (C1/C2): kafle osób, raty i wpłaty. */
  toBookingView(
    bill: BookingBill,
    items: ChangeItemDto[] = [],
    preview: ChangePreview | null = null,
    editing = false,
  ): BookingBillVm {
    const context = { bill, items, preview };
    const bookingBefore = bill.totals;
    const bookingAfter = bookingTotalAfter(bill, preview);

    const participants: PersonTileVm[] = bill.participants.map(
      (participant) => {
        const before = participantTotal(bill, participant.participantId);
        const delta = previewParticipantDelta(
          preview,
          participant.participantId,
        );
        const after = addMoneyLists(before, delta);
        const changeCount = countItemsForPerson(
          items,
          participant.participantId,
        );

        return {
          person: participantIdentity(participant),
          meta: editing
            ? [
                participantRoleDetail(participant.age),
                `${pluralizeChanges(changeCount)} w szkicu`,
              ]
            : [bill.tourName, participantRoleDetail(participant.age)],
          amount: editing ? delta : before,
          amountNote: editing
            ? formatMoneyTransition(before, after)
            : 'rachunek uczestnika',
          draftWithoutImpact:
            editing && changeCount > 0 && isZeroMoneyList(delta)
              ? true
              : undefined,
          withdrawn: participant.withdrawn || undefined,
        };
      },
    );

    const clientChangeCount = countItemsForPerson(items, null);
    const client: PersonTileVm = {
      person: clientIdentity(bill.client),
      meta: editing
        ? [
            `${pluralizeChanges(clientChangeCount)} w szkicu`,
            ...changedClientFields(bill, items),
          ]
        : [
            bill.client.email,
            ...(bill.client.phoneNumber
              ? [formatPhoneNumber(bill.client.phoneNumber)]
              : []),
          ],
      // Zapisujący nie ma rachunku: w edycji kafel mówi „bez wpływu na cenę”
      // słowem, więc kwota jest pusta zamiast udawać zero w wybranej walucie.
      amount: editing ? [] : paidTotal(bill),
      amountNote: editing ? 'bez wpływu na cenę' : 'wpłacone przez płatnika',
      amountMuted: editing || undefined,
      draftWithoutImpact: editing && clientChangeCount > 0 ? true : undefined,
    };

    const names = installmentNames(bill.installments);

    return {
      bookingId: bill.bookingId,
      clientId: bill.client.clientId,
      tourId: bill.tourId,
      bookingCode: bill.bookingCode,
      tourName: bill.tourName,
      dateRangeLabel: formatDateRange(bill.tourStartDate, bill.tourEndDate),
      status: bill.status,
      client,
      participants,
      totalValue: editing ? bookingAfter : bookingBefore,
      bookingWideSections: toSections(bill.bookingWideSections, null, context),
      installments: bill.installments.map((installment) =>
        toInstallment(installment, names),
      ),
      payments: bill.payments.map(toPayment),
    };
  },

  /** Wartość rezerwacji po uwzględnieniu szkicu — używana też przez panel boczny. */
  toBookingValueAfterDraft(
    bill: BookingBill,
    preview: ChangePreview | null,
  ): MoneyValueDto[] {
    return bookingTotalAfter(bill, preview);
  },
};
